import React, { useState, useEffect } from 'react';
import { Typography, Container, Box, CircularProgress, Alert, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';
import { CheckCircle as CheckCircleIcon, RadioButtonUnchecked as RadioButtonUncheckedIcon } from '@mui/icons-material';
import { db } from './firebase.ts';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useTheme } from './ThemeContext.tsx'; // Import useTheme
import MobileHeader from './components/MobileHeader'; // Import MobileHeader
import useMediaQuery from '@mui/material/useMediaQuery'; // Import useMediaQuery
import { useTheme as useMuiTheme } from '@mui/material/styles'; // Import useMuiTheme

import { User } from 'firebase/auth';

interface ReportTask {
  completed: boolean;
  description: string;
}

interface CompletedChecklistReport {
  id: string;
  checklistName: string;
  dateCompleted: string;
  timestamp: any; // Firestore Timestamp, can be converted to Date
  uploadedBy?: string;
  tasks: ReportTask[];
}

interface ReportsProps {
  user: User | null;
}

function Reports({ user }: ReportsProps) {
  const [reports, setReports] = useState<Record<string, CompletedChecklistReport[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<string[]>([]);

  const { theme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchAvailableDates = async () => {
      if (!user) return;
      try {
        const q = query(
          collection(db, 'completedChecklistReports'),
          where('userId', '==', user.uid),
          orderBy('dateCompleted', 'desc')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const dates = new Set<string>();
          snapshot.docs.forEach(doc => {
            dates.add(doc.data().dateCompleted as string);
          });
          const sortedDates = Array.from(dates).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
          setAvailableDates(sortedDates);
          if (sortedDates.length > 0 && !selectedDate) {
            setSelectedDate(sortedDates[0]); // Automatically select the most recent date
          }
        }, (err) => {
          console.error("Error fetching available dates:", err);
          setError("Failed to load available report dates.");
        });
        return () => unsubscribe();
      } catch (err) {
        console.error("Error fetching available dates:", err);
        setError("Failed to load available report dates.");
      }
    };

    fetchAvailableDates();
  }, [user, selectedDate]);

  useEffect(() => {
    if (!user || !selectedDate) {
      setReports({}); // Set to an empty object instead of an empty array
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'completedChecklistReports'),
      where('userId', '==', user.uid),
      where('dateCompleted', '==', selectedDate),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedReports: CompletedChecklistReport[] = snapshot.docs.map(doc => ({
        id: doc.id,
        checklistName: doc.data().checklistName,
        dateCompleted: doc.data().dateCompleted,
        timestamp: doc.data().timestamp,
        uploadedBy: doc.data().uploadedBy,
        tasks: doc.data().tasks,
      }));

      // Group reports by checklistName
      const groupedReports = fetchedReports.reduce((acc: Record<string, CompletedChecklistReport[]>, report: CompletedChecklistReport) => {
        if (!acc[report.checklistName]) {
          acc[report.checklistName] = [];
        }
        acc[report.checklistName].push(report);
        return acc;
      }, {});

      setReports(groupedReports);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching reports:", err);
      setError("Failed to load reports for this date.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, selectedDate]);

  const handleDateChange = (event: SelectChangeEvent<string>) => {
    setSelectedDate(event.target.value as string);
  };

  const formatDateForDisplay = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading && !selectedDate) { // Only show full loading if no date is selected yet
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ pt: 0, px: 0 }} maxWidth={false}>
      {isMobile && <MobileHeader title="Reports" />}

      <Box sx={{ mt: isMobile ? '70px' : '30px', px: isMobile ? 2 : 3, pb: isMobile ? 2 : 3 }}> {/* Adjusted mt */}
        <Box sx={{ my: 3 }}>
          <FormControl fullWidth variant="outlined" sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: theme.borderRadius,
            backgroundColor: theme.colors.whiteCardBackground,
            '& fieldset': {
              borderColor: theme.colors.borderColor,
            },
            '&:hover fieldset': {
              borderColor: theme.colors.darkText,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.colors.primaryRed,
            },
          },
          '& .MuiInputLabel-root': {
            color: theme.colors.lightText,
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.colors.primaryRed,
          },
          '& .MuiSelect-select': {
            color: theme.colors.darkText,
          },
          '& .MuiSvgIcon-root': {
            color: theme.colors.lightText,
          },
        }}>
          <InputLabel id="select-report-date-label">Select Report Date</InputLabel>
          <Select
            labelId="select-report-date-label"
            id="select-report-date"
            value={selectedDate}
            label="Select Report Date"
            onChange={handleDateChange}
            disabled={availableDates.length === 0 && !loading}
          >
            {availableDates.length === 0 && !loading ? (
              <MenuItem value="" sx={{ color: theme.colors.lightText }}>No dates available</MenuItem>
            ) : (
              availableDates.map((date) => (
                <MenuItem key={date} value={date} sx={{ color: theme.colors.darkText }}>
                  {formatDateForDisplay(date)}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>

      {loading && selectedDate && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      )}

      {!loading && !error && selectedDate && Object.keys(reports).length === 0 && (
        <Typography variant="body1" align="center" sx={{ mt: 2, color: theme.colors.lightText }}>
          No reports found for {formatDateForDisplay(selectedDate)}.
        </Typography>
      )}

      {!loading && !error && selectedDate && Object.keys(reports).length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.darkText, mb: 2 }}>
            Report for: {formatDateForDisplay(selectedDate)}
          </Typography>
          {Object.keys(reports).map((checklistName) => {
            const checklistReports = reports[checklistName];
            const latestReport = checklistReports[0];

            const totalTasks = latestReport.tasks.length;
            const completedTasks = latestReport.tasks.filter(task => task.completed).length;

            return (
              <Box
                key={checklistName}
                sx={{
                  backgroundColor: theme.colors.whiteCardBackground,
                  borderRadius: theme.borderRadius,
                  border: `1px solid ${theme.colors.borderColor}`,
                  p: 2,
                  mb: 2,
                  boxShadow: theme.boxShadow,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ color: theme.colors.darkText, fontWeight: 'bold' }}>{checklistName}</Typography>
                  <Typography variant="body2" sx={{ color: completedTasks === totalTasks ? theme.colors.greenSuccess : theme.colors.primaryRed }}>
                    {completedTasks}/{totalTasks} done
                  </Typography>
                </Box>
                {latestReport.uploadedBy && (
                  <Typography variant="body2" sx={{ color: theme.colors.lightText, mb: 1 }}>
                    Uploaded by: {latestReport.uploadedBy}
                  </Typography>
                )}
                <Box>
                  {latestReport.tasks.map((task, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {task.completed ? (
                        <CheckCircleIcon sx={{ mr: 1, fontSize: '1rem', color: theme.colors.greenSuccess }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ mr: 1, fontSize: '1rem', color: theme.colors.lightText }} />
                      )}
                      <Typography variant="body2" sx={{ color: theme.colors.darkText }}>{task.description}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      </Box> {/* Closing tag for the main content Box */}
    </Container>
  );
}

export default Reports;
